import type { ClosedSeason, Season, SeasonId, SeasonKind } from "@skladiscenje/contracts";

const MIN_YEAR = 2000;
const MAX_YEAR = 2100;

/**
 * Display label for a season.
 *
 * A closed season renders as `"<Kind> <Year>"`, capitalised (`"Letne
 * 2024"`, `"Zimske 2020"`). An **open** season has no `kind`/`year` — per
 * the current contract, a season is unnamed for as long as it's open and is
 * only named at the moment it closes (see {@link closeSeason}) — so this
 * renders it as `"Nova sezona"`, reusing the exact literal legacy used for
 * the same bucket (`DAO.NOVA_SEZONA`, DAO.cs:16), since it's the same
 * concept: the place new intake lands before it has a season name yet.
 */
export function seasonLabel(season: Pick<Season, "kind" | "year">): string {
  if (season.kind === null || season.year === null) {
    return "Nova sezona";
  }
  const kindLabel = season.kind === "letne" ? "Letne" : "Zimske";
  return `${kindLabel} ${season.year}`;
}

/**
 * Orders seasons the way `DAO.getSeasons` does (DAO.cs:46-69), worked out by
 * tracing its comparator by hand:
 *
 * ```
 * seasons.Sort((x, y) => {
 *     var s1 = x.Split(' '); var s2 = y.Split(' ');       // ["Letne", "2019"]
 *     if (s1[1] != s2[1]) return int(s1[1]).CompareTo(int(s2[1])); // by year, ascending
 *     else return -s1[0].CompareTo(s2[0]);                // by kind, DESCENDING alpha
 * });
 * seasons.Reverse();
 * ```
 *
 * Ascending sort puts, within a tied year, "Zimske" before "Letne" (because
 * `-("Letne".CompareTo("Zimske"))` is positive, so "Letne" sorts after
 * "Zimske"). `.Reverse()` then flips the *entire* list, which — because same
 * -year runs are contiguous — flips both the year order and the within-year
 * order. Net result: **year descending; within a year, Letne before
 * Zimske.**
 *
 * Worked example: ascending gives
 * `[Zimske 2019, Letne 2019, Zimske 2020, Letne 2020]`; reversed gives
 * `[Letne 2020, Zimske 2020, Letne 2019, Zimske 2019]`.
 *
 * The unnamed **open** season (`kind`/`year` both `null` — see
 * {@link seasonLabel}) has no date to sort by, and legacy's equivalent
 * (`"Nova sezona"`) was never part of this comparator at all — it was
 * excluded from `getSeasons`'s result and manually prepended *first* by
 * `getSeasonsWithNew` (DAO.cs:71-77): `seasons.Insert(0, NOVA_SEZONA)`.
 * This function reproduces that placement directly: **the unnamed open
 * season sorts first**, ahead of every named/closed season, because it's
 * the active bucket the counter cares about most. (At most one season
 * should ever be open at a time — see {@link canCloseSeason} — but if more
 * than one somehow is, they're kept in input order, ahead of the named
 * ones, rather than this function silently picking a winner.)
 */
export function sortSeasons(seasons: readonly Season[]): Season[] {
  const unnamed = seasons.filter((s) => s.kind === null || s.year === null);
  const named = seasons.filter((s) => s.kind !== null && s.year !== null);

  const sortedNamed = [...named].sort((a, b) => {
    // a.year/b.year are non-null here by construction of `named` above.
    const yearDiff = (b.year as number) - (a.year as number); // descending
    if (yearDiff !== 0) return yearDiff;
    // Within a year: Letne before Zimske.
    if (a.kind === b.kind) return 0;
    return a.kind === "letne" ? -1 : 1;
  });

  return [...unnamed, ...sortedNamed];
}

/** Why a season cannot be closed right now. */
export type CloseSeasonRejectionReason =
  | "not-found"
  | "not-open"
  | "multiple-open-seasons"
  | "duplicate-target-label"
  | "invalid-kind"
  | "invalid-year";

export type CloseSeasonCheck = { ok: true } | { ok: false; reason: CloseSeasonRejectionReason };

/** The kind/year the operator picked in the close-season dialog (MainWindow.xaml:80-81) to name the outgoing season. */
export interface SeasonName {
  kind: SeasonKind;
  year: number;
}

export interface CloseSeasonInput {
  /** All known seasons — used to find the current open one and check for label clashes. */
  seasons: readonly Season[];
  /** The season being closed. Must currently be the (sole) open season. */
  openSeasonId: SeasonId;
  /** What to name the season being closed. Both fields are required — an open season cannot close without being named. */
  name: SeasonName;
}

/**
 * Checks the invariants around closing a season, without performing the
 * transition. Exposed separately from {@link closeSeason} so a caller (e.g.
 * a UI) can disable/validate a "close season" action before the user commits
 * to it.
 *
 * Invariants:
 *
 * - `openSeasonId` must refer to a season that exists and has `isOpen: true`
 *   (DAO.cs has no real analogue — legacy's "current season" was implicit,
 *   a single sentinel string rather than a row that could be missing).
 * - Exactly one season may be open at a time — if the season list already
 *   shows more than one `isOpen: true` row, that's a corrupt state and the
 *   close is refused rather than silently picking one.
 * - `name.kind`/`name.year` must both be present and valid — a season
 *   cannot close without being named. `year` is bounds-checked against the
 *   same range the `season` contract enforces (2000-2100).
 * - The resulting label (`kind`+`year`) must not already belong to an
 *   existing closed season. Legacy enforced exactly this, client-side,
 *   before ever calling `finishSeason`:
 *   `if (!seasonComboBox.Items.Contains(name))` (MainWindow.xaml.cs:317) —
 *   you cannot reuse a season name that's already in the list. Ported here
 *   so the rule holds server-side too.
 */
export function canCloseSeason(input: CloseSeasonInput): CloseSeasonCheck {
  const openSeasons = input.seasons.filter((s) => s.isOpen);
  if (openSeasons.length > 1) {
    return { ok: false, reason: "multiple-open-seasons" };
  }

  const target = input.seasons.find((s) => s.id === input.openSeasonId);
  if (!target) {
    return { ok: false, reason: "not-found" };
  }
  if (!target.isOpen) {
    return { ok: false, reason: "not-open" };
  }

  if (input.name.kind !== "letne" && input.name.kind !== "zimske") {
    return { ok: false, reason: "invalid-kind" };
  }
  if (
    !Number.isInteger(input.name.year) ||
    input.name.year < MIN_YEAR ||
    input.name.year > MAX_YEAR
  ) {
    return { ok: false, reason: "invalid-year" };
  }

  const clash = input.seasons.some(
    (s) => s.id !== target.id && s.kind === input.name.kind && s.year === input.name.year,
  );
  if (clash) {
    return { ok: false, reason: "duplicate-target-label" };
  }

  return { ok: true };
}

/** Patch to apply to the season being closed — satisfies the stricter `closedSeason` schema. */
export type SeasonClosePatch = { seasonId: SeasonId } & Omit<ClosedSeason, "id" | "createdAt">;

/** Fields needed to create the freshly-opened, still-unnamed season. The caller assigns the id. */
export type SeasonOpenDraft = Omit<Season, "id">;

export interface SeasonTransitionPlan {
  close: SeasonClosePatch;
  open: SeasonOpenDraft;
}

/**
 * Describes the season state transition: close the current open season,
 * naming it with the `kind`/`year` the operator chose, and open a fresh
 * **unnamed** season in its place for new intake to land in. Pure
 * description — the caller applies both writes atomically (patch the old
 * season, create the new row).
 *
 * This is a metadata change, not a data migration: legacy's `finishSeason`
 * (DAO.cs:79-92) instead re-wrote the `season` string on *every row*
 * currently tagged "Nova sezona" to the newly chosen name. That rewrite
 * disappears entirely here — rows reference a season by id, so closing a
 * season never touches `warehouseItem` rows at all; only the season row's
 * own `kind`/`year`/`isOpen`/`closedAt` change, and a new season row is
 * created for future intake.
 *
 * Note DAO.cs:80 — `Properties.Settings.Default.Seasons.Add("seasonName")`
 * — adds the *literal string* `"seasonName"` instead of the `seasonName`
 * parameter, a real bug in the legacy code (a local app-settings cache ends
 * up permanently polluted with the string `"seasonName"` instead of the
 * actual season). This function does not reproduce it; there is no
 * equivalent local cache to pollute.
 *
 * Does not itself call {@link canCloseSeason} — callers should check that
 * first and treat a failing check as "do not call this."
 */
export function closeSeason(input: CloseSeasonInput, now: Date): SeasonTransitionPlan {
  return {
    close: {
      seasonId: input.openSeasonId,
      kind: input.name.kind,
      year: input.name.year,
      isOpen: false,
      closedAt: now,
    },
    open: {
      kind: null,
      year: null,
      isOpen: true,
      closedAt: null,
      createdAt: now,
    },
  };
}
